import React, { useEffect, useRef, useState } from 'react';

const CursorParticle = () => {
    const circlesRef = useRef([]);
    const coordsRef = useRef({ x: 0, y: 0 });
    const animationFrameRef = useRef(null);
    const dotCountRef = useRef(20);
    // Hide particles on phone only (< 768px); tablet/iPad/desktop get particles
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' && window.innerWidth < 768
    );
    // Tablet/iPad (768–1367): fewer particles to avoid lag; desktop & phone unchanged.
    const [isTablet, setIsTablet] = useState(() =>
        typeof window !== 'undefined' && window.innerWidth >= 768 && window.innerWidth < 1368
    );

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        const checkTablet = () => {
            const w = window.innerWidth;
            setIsTablet(w >= 768 && w < 1368);
        };
        window.addEventListener('resize', checkMobile);
        window.addEventListener('resize', checkTablet);
        return () => {
            window.removeEventListener('resize', checkMobile);
            window.removeEventListener('resize', checkTablet);
        };
    }, []);

    useEffect(() => {
        if (isMobile || isTablet) return; // No particles on phone or tablet/iPad; desktop only
        const circles = circlesRef.current;
        const coords = coordsRef.current;
        const count = dotCountRef.current;

        // Orange color gradient for particles
        const colors = [
            "#FF7828",
            "#ff8a3d",
            "#ff9c52",
            "#ffae67",
            "#ffc07c",
            "#ffd291",
            "#ffe4a6",
            "#fff6bb",
        ];

        // Initialize circles (only up to current count)
        for (let i = 0; i < count; i++) {
            const circle = circles[i];
            if (circle) {
                circle.x = 0;
                circle.y = 0;
                circle.style.backgroundColor = colors[i % colors.length];
            }
        }

        // Mouse move handler
        const handleMouseMove = (e) => {
            coords.x = e.clientX;
            coords.y = e.clientY;
        };

        // Check if cursor is over cards, footer, or form sections
        const isOverCardOrFooter = (x, y) => {
            const element = document.elementFromPoint(x, y);
            if (!element) return false;
            
            // Check element and all its parents
            let current = element;
            while (current && current !== document.body) {
                const className = current.className?.toString() || '';
                const id = current.id || '';
                const tagName = current.tagName?.toLowerCase() || '';
                
                // Check for What We Offer cards specifically - hide on cards only
                // Check for data attribute first (most reliable)
                if (current.getAttribute && current.getAttribute('data-what-we-offer-card') === 'true') {
                    return true; // Hide particles on What We Offer cards
                }
                
                // Also check by traversing up to find card with data attribute
                const whatWeOfferCard = current.closest ? current.closest('[data-what-we-offer-card="true"]') : null;
                if (whatWeOfferCard) {
                    return true; // Hide particles on What We Offer cards
                }
                
                // Check for People Behind cards specifically - hide on cards only
                // Check for data attribute first (most reliable)
                if (current.getAttribute && current.getAttribute('data-people-behind-card') === 'true') {
                    return true; // Hide particles on People Behind cards
                }
                
                // Also check by traversing up to find card with data attribute
                const peopleBehindCard = current.closest ? current.closest('[data-people-behind-card="true"]') : null;
                if (peopleBehindCard) {
                    return true; // Hide particles on People Behind cards
                }
                
                // Fallback: Check for What We Offer cards by checking computed styles and dimensions
                const computedStyle = window.getComputedStyle(current);
                const bgColor = computedStyle.backgroundColor;
                // Check if background color matches the card colors (rgb(8, 14, 31) or rgb(10, 18, 37))
                if (bgColor.includes('rgb(8, 14, 31)') || bgColor.includes('rgb(10, 18, 37)')) {
                    const whatWeOfferSection = current.closest ? current.closest('#what-we-offer, .what-we-offer-section') : null;
                    if (whatWeOfferSection) {
                        // Check if it's actually a card (has specific height/width characteristics)
                        const rect = current.getBoundingClientRect();
                        if (rect.height > 300 && rect.width > 500) {
                            return true; // Hide particles on What We Offer cards
                        }
                    }
                }
                
                // Fallback: Check for People Behind cards by checking white background and dimensions
                // Check if it's a white card (rgb(255, 255, 255) or similar)
                if (bgColor.includes('rgb(255, 255, 255)') || bgColor.includes('rgb(249, 250, 251)')) {
                    const rect = current.getBoundingClientRect();
                    // Check if it's a card (has specific dimensions - around 400x450 for People Behind cards)
                    if (rect.height > 350 && rect.height < 500 && rect.width > 300 && rect.width < 500) {
                        // Check if it's within People Behind section
                        const peopleBehindSection = current.closest ? current.closest('.people-behind-section') : null;
                        if (peopleBehindSection) {
                            return true; // Hide particles on People Behind cards
                        }
                    }
                }
                
                // Check for expanded member detail card (gray-900 background)
                if (bgColor.includes('rgb(17, 24, 39)') || bgColor.includes('rgb(31, 41, 55)')) {
                    const rect = current.getBoundingClientRect();
                    // Expanded card is large (around 1500x726)
                    if (rect.height > 600 && rect.width > 1000) {
                        const peopleBehindSection = current.closest ? current.closest('.people-behind-section') : null;
                        if (peopleBehindSection) {
                            return true; // Hide particles on expanded People Behind card
                        }
                    }
                }
                
                // Check for Footer - by tag name
                if (tagName === 'footer') {
                    return true;
                }
                
                // Check for Footer - by class or ID
                if (className.includes('footer') || 
                    className.includes('Footer') ||
                    id.includes('footer')) {
                    return true;
                }
                
                // Check for Let's Connect form container - hide on entire form container
                if (className.includes('bg-gray-100') && className.includes('backdrop-blur-sm')) {
                    return true;
                }
                
                // Check if inside Let's Connect form container
                const formContainer = current.closest ? current.closest('div.bg-gray-100.backdrop-blur-sm') : null;
                if (formContainer) {
                    return true;
                }
                
                // Check if directly inside form input elements
                if (tagName === 'input' || 
                    tagName === 'textarea' || 
                    tagName === 'select' || 
                    (tagName === 'button' && current.type === 'submit')) {
                    return true;
                }
                
                // Check if inside a form element
                if (tagName === 'form') {
                    return true;
                }
                
                current = current.parentElement;
            }
            return false;
        };

        // Animation function (only animate up to dotCountRef.current)
        const animateCircles = () => {
            let x = coords.x;
            let y = coords.y;
            const count = dotCountRef.current;

            // Hide circles if over cards/footer
            const shouldHide = isOverCardOrFooter(x, y);

            for (let index = 0; index < count; index++) {
                const circle = circles[index];
                if (!circle) continue;

                if (shouldHide) {
                    circle.style.opacity = '0';
                    circle.style.pointerEvents = 'none';
                } else {
                    circle.style.opacity = '1';
                    circle.style.pointerEvents = 'none';
                }

                circle.style.left = x - 12 + "px";
                circle.style.top = y - 12 + "px";
                circle.style.scale = (count - index) / count;

                circle.x = x;
                circle.y = y;

                const nextCircle = circles[index + 1] || circles[0];
                if (nextCircle) {
                    x += (nextCircle.x - x) * 0.3;
                    y += (nextCircle.y - y) * 0.3;
                }
            }

            animationFrameRef.current = requestAnimationFrame(animateCircles);
        };

        window.addEventListener("mousemove", handleMouseMove, { passive: true });
        animateCircles();

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [isMobile, isTablet]);

    if (isMobile || isTablet) return null; // Desktop only: show particles; phone & tablet/iPad: none

    const dotCount = 20; // Desktop only
    dotCountRef.current = dotCount;
    return (
        <>
            {[...Array(dotCount)].map((_, index) => (
                <div
                    key={index}
                    ref={(el) => (circlesRef.current[index] = el)}
                    className="cursor-circle"
                />
            ))}
        </>
    );
};

export default CursorParticle;

